// Aceleres 2026-08-25 -- os DOIS sistemas de coordenadas do painel.
//
// ATUALIZACAO 2026-08-31: o `html { zoom: .8 }` foi RETIRADO a pedido. Hoje
// `getDashboardZoom()` devolve 1 e todas as conversoes abaixo sao identidade —
// visual e layout coincidem. O helper fica de proposito: a regra vinha do
// DASHBOARD_SCRIPTS (installation_configs, no banco), nao deste repositorio,
// entao ela pode voltar sem passar por nenhum grep de CSS daqui. Quem chama
// estas funcoes continua correto nos dois mundos; quem escrever conta a mao
// volta a quebrar no dia em que houver escala global de novo.
//
// O texto abaixo descreve o comportamento COM zoom.
//
// O `zoom` parte o ecra em dois espacos de medida:
//
//   VISUAL  -- o que o utilizador ve e o que o browser REPORTA:
//              `clientX`/`clientY` do rato, `getBoundingClientRect()`,
//              `window.innerWidth`/`innerHeight`, `visualViewport`.
//   LAYOUT  -- o que se ESCREVE em CSS: `left`, `top`, `width`, `height`,
//              `translate`, `scrollLeft`. O browser desenha cada comprimento
//              multiplicado pelo zoom.
//
//   visual = layout x zoom
//
// Escrever uma coordenada visual (o `clientX` do rato) direto num `left` poe o
// elemento a 80% do caminho ate ao cursor. O erro nao e constante: e 20% da
// distancia a origem do ecra -- 20px num clique encostado a esquerda, 280px num
// clique a x=1400. Foi essa proporcionalidade que fez o problema parecer
// intermitente ("as vezes abre no sitio certo"): perto do canto superior
// esquerdo o desvio e pequeno demais para se notar.
//
// O mesmo vale para arrastos: um `dx` visual somado a uma largura em layout faz
// o divisor andar 1/0.8 = 1.25x mais depressa que o rato.
//
// Regra pratica:
//   * comparacoes entre rato, rects e viewport -> tudo em VISUAL, sem converter;
//   * na hora de escrever em CSS -> `layoutPx()` / `visualToLayout()`;
//   * constantes de tamanho declaradas em CSS (uma largura `w-[240px]`) sao
//     LAYOUT: para as comparar com rects, passe por `layoutToVisual()`.

export function getDashboardZoom() {
  if (typeof document === 'undefined') return 1;
  const zoom = parseFloat(getComputedStyle(document.documentElement).zoom);
  return Number.isFinite(zoom) && zoom > 0 ? zoom : 1;
}

// Coordenada/comprimento visual -> o numero a escrever em CSS.
export function visualToLayout(value, zoom = getDashboardZoom()) {
  return value / zoom;
}

// Comprimento declarado em CSS -> o tamanho que ele ocupa no ecra.
export function layoutToVisual(value, zoom = getDashboardZoom()) {
  return value * zoom;
}

// Acucar para `:style` — devolve ja com a unidade.
export function layoutPx(value, zoom = getDashboardZoom()) {
  return `${visualToLayout(value, zoom)}px`;
}

// O retangulo visivel, em espaco VISUAL, para clamps de "nao sair do ecra".
//
// `window.innerWidth`/`innerHeight` e `visualViewport` ja vem em visual mesmo
// com o zoom aplicado (medido no Chrome 151, que e o que os utilizadores tem);
// preferimos o `visualViewport` porque no telemovel ele desconta o teclado.
export function getVisualViewport() {
  const zoom = getDashboardZoom();
  const vv = typeof window !== 'undefined' ? window.visualViewport : null;

  return {
    zoom,
    width: vv?.width || window.innerWidth,
    height: vv?.height || window.innerHeight,
  };
}

// Posicao do rato em espaco VISUAL, pronta para comparar com rects.
//
// Usa sempre `clientX`/`clientY`, nunca `pageX`/`pageY`: quem consome isto
// posiciona elementos `position: fixed`, que sao relativos a viewport. Num
// documento com scroll o `pageX` traz o offset de scroll a mais e o menu foge
// para fora do ecra. (O painel raramente rola o documento, o que mantinha esse
// erro adormecido -- mas adormecido nao e corrigido.)
export function getPointerViewportPosition(event) {
  const point = event?.touches?.[0] || event?.changedTouches?.[0] || event;

  return {
    x: Number.isFinite(point?.clientX) ? point.clientX : 0,
    y: Number.isFinite(point?.clientY) ? point.clientY : 0,
  };
}

// Encaixa um retangulo dentro da viewport visivel. Entra e sai tudo em VISUAL.
export function clampToVisualViewport(
  x,
  y,
  width,
  height,
  padding = 8,
  viewport = getVisualViewport()
) {
  const maxLeft = Math.max(padding, viewport.width - width - padding);
  const maxTop = Math.max(padding, viewport.height - height - padding);

  return {
    left: Math.min(Math.max(x, padding), maxLeft),
    top: Math.min(Math.max(y, padding), maxTop),
  };
}
